import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent, MODAL_DATA } from '@stockaid/core';
import { tap } from 'rxjs/operators';
import { MetaDataField } from 'src/app/core/infrastructure/classes/meta-data-field';
import { LookUpConfig } from 'src/app/core/infrastructure/classes/metadataDisplayControls/support/look-up-config';
import { uniqBy } from 'lodash';
import { BomService } from 'src/app/core/services/bom.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-create-bom-modal',
  templateUrl: './create-bom-modal.component.html',
  styleUrls: ['./create-bom-modal.component.scss'],
})
export class CreateBomModalComponent extends BaseComponent implements OnInit {
  formGroup: FormGroup;
  parentLookupConfig: LookUpConfig;
  childLookupConfig: LookUpConfig;
  isChildrenDuplicated = false;
  isLoading = false;

  constructor(
    @Inject(MODAL_DATA) public data,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private bomService: BomService
  ) {
    super();

    (data.metadataFields as MetaDataField[]).forEach((field) => {
      switch (field.field) {
        case 'parentName':
          this.parentLookupConfig = field.displayControl.lookUpConfig;
          break;

        case 'childName':
          this.childLookupConfig = field.displayControl.lookUpConfig;
          break;
      }
    });
  }

  get parentNameControl() {
    return this.formGroup?.get('parentName');
  }

  get parentKeyControl() {
    return this.formGroup?.get('parentKey');
  }

  get childFormGroups() {
    return (this.formGroup?.get('children') as FormArray)?.controls;
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.handleComponentChanges();
  }

  removeChild(childIdx: number) {
    const childrenFormArray = this.formGroup?.get('children') as FormArray;
    childrenFormArray.removeAt(childIdx);
  }

  addChild() {
    const childrenFormArray = this.formGroup?.get('children') as FormArray;
    childrenFormArray.push(
      this.fb.group({
        childKey: ['', Validators.required],
        childName: ['', Validators.required],
        qty: [
          '',
          [Validators.required, Validators.min(1), Validators.max(99999)],
        ],
      })
    );
  }

  save() {
    if (this.formGroup.invalid || this.isChildrenDuplicated || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const newBoms = this.formGroup.value.children.map((child) => ({
      ...child,
      parentKey: this.formGroup.value.parentKey,
      parentName: this.formGroup.value.parentName,
    }));
    this.bomService
      .createMultipleBoms(newBoms)
      .pipe(
        this.autoCleanUp(),
        tap(
          ({ errors }) => {
            if (errors?.length) {
              Swal.fire({
                title: 'Error',
                html: `
                <div>
                  <ul>
                    ${errors.map((error) => `<li>${error}</li>`).join('')}
                  </ul>
                </div>
              `,
                type: 'error',
              });
              this.activeModal.dismiss();
              return;
            }

            this.activeModal.close();
          },
          () => {
            this.isLoading = false;
          }
        )
      )
      .subscribe();
  }

  private initFormGroup() {
    this.formGroup = this.fb.group({
      parentKey: ['', Validators.required],
      parentName: ['', Validators.required],
      children: this.fb.array([
        this.fb.group({
          childKey: ['', Validators.required],
          childName: ['', Validators.required],
          qty: [
            '',
            [Validators.required, Validators.min(1), Validators.max(99999)],
          ],
        }),
      ]),
    });
  }

  private handleComponentChanges() {
    this.formGroup
      .get('children')
      .valueChanges.pipe(
        this.autoCleanUp(),
        tap((children) => {
          const uniqChildren = uniqBy(children, 'childKey');
          const isFullFilled = !children.some(child => !child.childKey);

          this.isChildrenDuplicated = isFullFilled && uniqChildren.length < children.length;
        })
      )
      .subscribe();
  }
}
